import React, { ReactNode, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import DropShadow from '../../atoms/drop-shadow/DropShadow';
import CloseIcon from '../../atoms/icons/CloseIcon';

import { isBrowser } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import elevations from '../../../styles/elevations';
import Spacings from '../../../styles/Spacings';

interface Props {
  show?: boolean;
  children: ReactNode;
  onRequestCloseHandler: (close: boolean) => void;
}

const Modal = ({ show = false, children, onRequestCloseHandler }: Props) => {
  const modalWrapperRef = useRef(null);
  const modalContentRef = useRef(null);

  const modalContent = show ? (
    <DropShadow show={show} onRequestClose={() => onRequestCloseHandler(true)}>
      <MobileModalTopbar>
        <CancelButton
          data-testid="close-button"
          aria-label="Sluit modal"
          onClick={() => {
            onRequestCloseHandler(true);
          }}
        >
          <CloseIcon
            ariaHidden
            color={Colors.abnNeutral200}
            width="14"
            height="14"
          />
        </CancelButton>
      </MobileModalTopbar>
      <ModalWrapper
        role="dialog"
        aria-hidden="false"
        data-testid="modal"
        ref={modalWrapperRef}
        onMouseDown={(event) => {
          if (event.target === modalWrapperRef.current) {
            onRequestCloseHandler(true);
          }
        }}
      >
        <StyledModal ref={modalContentRef}>
          <TopBar>
            <CancelButton
              data-testid="close-button"
              aria-label="Sluit modal"
              onClick={() => onRequestCloseHandler(true)}
            >
              <CloseIcon
                ariaHidden
                color={Colors.abnNeutral200}
                width="14"
                height="14"
              />
            </CancelButton>
          </TopBar>
          <ModalBody>{children}</ModalBody>
          <BottomBar />
        </StyledModal>
      </ModalWrapper>
    </DropShadow>
  ) : null;

  return show && isBrowser
    ? ReactDOM.createPortal(
        modalContent,
        document.getElementById('modal-root') as HTMLElement
      )
    : null;
};

const MobileModalTopbar = styled.div`
  background: linear-gradient(
    180deg,
    #ffffff 18.75%,
    #ffffff 49.48%,
    rgba(255, 255, 255, 0.9) 69.79%,
    rgba(255, 255, 255, 0) 100%
  );
  display: flex;
  height: 72px;
  justify-content: end;
  justify-items: right;
  position: fixed;
  right: 10px;
  top: 0;
  width: 100%;
  z-index: 100;
  ${mq.from.breakpoint.M`
    display: none;
  `}
`;

const BottomBar = styled.div`
  display: none;

  ${mq.from.breakpoint.M`
    background-image: ${({ theme }) =>
      theme.elevations?.elevationTextBoxFadeBottom ||
      elevations.elevationTextBoxFadeBottom}
    z-index: 100;
    display: flex;
    height: 24px;
    justify-content: end;
    position: absolute;
    right: 10px;
    bottom: 0;
    width: 100%;`}
`;

const TopBar = styled.div`
  display: none;

  ${mq.from.breakpoint.M`
    z-index: 100;
    display: flex;
    justify-content: end;
    position: absolute;
    right: 10px;
    top: 0;
    height: 48px;
    width: 100%;`}
`;

const CancelButton = styled.a`
  align-items: center;
  cursor: pointer;
  display: flex;
  height: 48px;
  justify-content: center;
  line-height: 0;
  position: absolute;
  right: -10px;
  text-decoration: none;
  width: 48px;

  ${mq.from.breakpoint.M`
    top: 0;
    right: -10px;
    position: absolute;
  `}
`;

const ModalWrapper = styled.div`
  background-color: ${Colors.white};
  bottom: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100vh;
  overflow-y: scroll;
  padding: 16px;
  position: fixed;
  right: 0;
  top: 0;
  width: 100vw;
  z-index: 99;

  ${mq.to.breakpoint.M`
    min-height: 100vh;
    &:before {
      background-image: ${({ theme }) =>
        theme.elevations?.elevationTextBoxFadeBottom ||
        elevations.elevationTextBoxFadeBottom};
      bottom: 0;
      display: flex;
      position: fixed;
      right: 10px;
      width: 100%;
      z-index: 100;
      height: 48px;
      content: ' ';
    }
  `}

  ${mq.from.breakpoint.M`
    padding: 0;
    background-color: transparent;
    overflow-y: none;
    align-items: center;
    justify-content: center;
  `}
`;

const StyledModal = styled.div`
  align-items: start;
  background-color: ${Colors.white};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 48px 0;
  position: relative;
  width: 100%;
  z-index: 99;

  ${mq.from.breakpoint.M`
    box-shadow: ${elevations.abnElevationDialogueModal};
    margin: ${Spacings.S};
    max-height: 75vh;
    overflow: hidden;
    width: 80vw;
    max-width: 640px;
  `}
`;

const ModalBody = styled.div`
  overflow-y: auto;
  width: inherit;

  ${mq.from.breakpoint.M`
    position: relative;
    width: calc(100% - ${Spacings.L});
    padding: ${Spacings.S};
  `}
`;

export default Modal;
